(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-bt-urun-sinerji]';
  const CONFIG_SELECTOR = '[data-btus-config]';

  const toBool = (value) => value === true || value === 'true';

  const unique = (arr) => {
    return Array.from(new Set((arr || []).filter(Boolean)));
  };

  const KEY_ALIASES = {
    demir: 'iron',
    iron: 'iron',
    ferrous: 'iron',

    kalsiyum: 'calcium',
    calcium: 'calcium',

    magnezyum: 'magnesium',
    magnesium: 'magnesium',

    cinko: 'zinc',
    çinko: 'zinc',
    zinc: 'zinc',

    bakir: 'copper',
    bakır: 'copper',
    copper: 'copper',

    selenyum: 'selenium',
    selenium: 'selenium',

    iyot: 'iodine',
    iodine: 'iodine',

    omega3: 'omega3',
    omega_3: 'omega3',
    'omega-3': 'omega3',

    probiyotik: 'probiotic',
    probiotic: 'probiotic',

    prebiyotik: 'prebiotic',
    prebiotic: 'prebiotic',

    kolajen: 'collagen',
    collagen: 'collagen',
    hidrolize_kolajen_tip_i: 'collagen',
    hidrolize_kolajen_tip_ii: 'collagen',
    hidrolize_kolajen_tip_iii: 'collagen',
    hidrolize_kolajen_tip_i_tip_iii: 'collagen',
    hidrolize_kolajen_tip_i_tip_ii_tip_iii: 'collagen',
    tip_i_kolajen_peptitleri: 'collagen',
    tip_ii_kolajen_peptitleri: 'collagen',
    tip_iii_kolajen_peptitleri: 'collagen',
    tip_i_iii_kolajen_peptitleri: 'collagen',
    tip_i_ii_iii_kolajen_peptitleri: 'collagen',
    tip_i_v_x_kolajen_peptitleri: 'collagen',
    tip_v_x_kolajen_peptitleri: 'collagen',
    balik_kolajeni: 'collagen',

    d_vitamini: 'vitamin_d3',
    vitamin_d: 'vitamin_d3',
    vitamin_d3: 'vitamin_d3',

    k2: 'vitamin_k2',
    vitamin_k2: 'vitamin_k2',

    b6: 'vitamin_b6',
    vitamin_b6: 'vitamin_b6',
    b6_vitamini: 'vitamin_b6',

    b12: 'vitamin_b12',
    vitamin_b12: 'vitamin_b12',
    b12_vitamini: 'vitamin_b12',

    c_vitamini: 'vitamin_c',
    vitamin_c: 'vitamin_c',

    e_vitamini: 'vitamin_e',
    vitamin_e: 'vitamin_e',

    a_vitamini: 'vitamin_a',
    vitamin_a: 'vitamin_a'
  };

  const normalizeKey = (key) => {
    const normalized = String(key || '')
      .trim()
      .toLowerCase();

    return KEY_ALIASES[normalized] || normalized;
  };

  const hasCollectionMatch = (productCollections, allowedCollections) => {
    if (!Array.isArray(allowedCollections) || !allowedCollections.length) {
      return true;
    }

    if (!Array.isArray(productCollections) || !productCollections.length) {
      return false;
    }

    const productCollectionSet = new Set(
      productCollections.map(normalizeKey).filter(Boolean)
    );

    return allowedCollections
      .map(normalizeKey)
      .filter(Boolean)
      .some((collectionHandle) => productCollectionSet.has(collectionHandle));
  };

  const normalizeMatchMode = (value) => {
    const normalized = normalizeKey(value);

    if (
      [
        'tum_etken_maddeler',
        'tum_etken_maddeler_birlikte_olmali',
        'tüm_etken_maddeler',
        'hepsi',
        'all',
        'all_ingredients',
        'require_all'
      ].includes(normalized)
    ) {
      return 'all_ingredients';
    }

    return 'target_only';
  };

  const getRequiredKeysForSynergy = (match) => {
    const mode = normalizeMatchMode(match?.rule?.matchMode);

    if (mode === 'all_ingredients') {
      return [
        normalizeKey(match?.rule?.a),
        normalizeKey(match?.rule?.b)
      ].filter(Boolean);
    }

    return [normalizeKey(match?.targetKey)].filter(Boolean);
  };

  const normalizeBrand = (brand) => {
    if (!brand) return '';

    if (typeof brand === 'object') {
      const possibleValue =
        brand.title ||
        brand.name ||
        brand.ad ||
        brand.marka_adi ||
        brand.value ||
        '';

      return normalizeBrand(possibleValue);
    }

    return String(brand)
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
  };

  const getCurrentVariantId = (root, fallbackId) => {
    const productSection = root.closest('section');
    const form =
      productSection?.querySelector('form[action*="/cart/add"]') ||
      document.querySelector('form[action*="/cart/add"]');

    const idInput = form?.querySelector('[name="id"]');

    return idInput?.value ? String(idInput.value) : String(fallbackId || '');
  };

  const severityLabel = (value) => {
    if (value === 'yuksek') return 'Önemli';
    if (value === 'orta') return 'Dikkat';
    if (value === 'dusuk') return 'Bilgi';

    return '';
  };

  const findMatchingRules = (rules, activeKeys) => {
    const active = new Set((activeKeys || []).map(normalizeKey));
    const warnings = [];
    const synergies = [];

    (rules || []).forEach((rule) => {
      const a = normalizeKey(rule.a);
      const b = normalizeKey(rule.b);

      if (!a) return;

      const hasA = active.has(a);
      const hasB = b ? active.has(b) : false;

      if (rule.type === 'negatif') {
        if ((b && (hasA || hasB)) || (!b && hasA)) {
          warnings.push({
            ...rule,
            matchedKey: hasA ? a : b,
            relatedKey: hasA ? b : a
          });
        }

        return;
      }

      if (rule.type === 'pozitif') {
        if (!a || !b) return;

        const trigger = normalizeKey(rule.trigger || a);
        const target = trigger === a ? b : a;

        if (active.has(trigger) && !active.has(target)) {
          synergies.push({
            rule,
            targetKey: target,
            sourceKey: trigger
          });
        }
      }
    });

    warnings.sort((r1, r2) => {
      return Number(r1.priority || 50) - Number(r2.priority || 50);
    });

    synergies.sort((r1, r2) => {
      return Number(r1.rule.priority || 50) - Number(r2.rule.priority || 50);
    });

    return {
      warnings,
      synergies
    };
  };

  const getResponsiveMaxProducts = (settings) => {
    const fallback = Math.max(1, Number(settings.maxProducts || 2));

    if (typeof window === 'undefined' || !window.matchMedia) {
      return fallback;
    }

    if (window.matchMedia('(max-width: 767px)').matches) {
      return Math.max(1, Number(settings.maxProductsMobile || fallback));
    }

    if (window.matchMedia('(max-width: 989px)').matches) {
      return Math.max(1, Number(settings.maxProductsTablet || fallback));
    }

    return Math.max(1, Number(settings.maxProductsDesktop || fallback));
  };

  const getCandidateProductPool = (
    synergies,
    candidates,
    currentKeys,
    settings
  ) => {
    const current = new Set((currentKeys || []).map(normalizeKey));
    const pool = [];
    const seenHandles = new Set();
    const seenBrands = new Set();
    const repeatBrand = toBool(settings.repeatBrand);

    synergies.forEach((match) => {
      const target = normalizeKey(match.targetKey);

      const availableCandidates = (candidates || []).filter((candidate) => {
        if (!candidate || !candidate.handle) return false;
        if (seenHandles.has(candidate.handle)) return false;

        if (
          toBool(settings.stockFilter) &&
          !candidate.available &&
          settings.outOfStockBehavior === 'gizle'
        ) {
          return false;
        }

        const normalizedCandidateKeys = (candidate.keys || []).map(normalizeKey);
        const requiredKeys = getRequiredKeysForSynergy(match);

        if (!requiredKeys.every((requiredKey) => normalizedCandidateKeys.includes(requiredKey))) {
          return false;
        }

        if (!hasCollectionMatch(candidate.collections, match.rule?.allowedCollections)) {
          return false;
        }

        const hasOnlyCurrentKnownKeys = normalizedCandidateKeys.every((key) => {
          return current.has(normalizeKey(key));
        });

        return !hasOnlyCurrentKnownKeys;
      });

      availableCandidates.forEach((candidate) => {
        const normalizedCandidateBrand = normalizeBrand(candidate.brand);

        if (
          !repeatBrand &&
          normalizedCandidateBrand &&
          seenBrands.has(normalizedCandidateBrand)
        ) {
          return;
        }

        seenHandles.add(candidate.handle);

        if (normalizedCandidateBrand) {
          seenBrands.add(normalizedCandidateBrand);
        }

        pool.push({
          ...candidate,
          synergyRule: match.rule,
          synergyTargetKey: target
        });
      });
    });

    return pool;
  };

  const getProductsForPage = (pool, settings, pageIndex) => {
    const max = getResponsiveMaxProducts(settings);

    if (!Array.isArray(pool) || !pool.length) {
      return [];
    }

    const start = (pageIndex * max) % pool.length;
    const products = [];

    for (let index = 0; index < max && index < pool.length; index += 1) {
      products.push(pool[(start + index) % pool.length]);
    }

    return products;
  };


  const updateAnchorBlocks = (root, isVisible) => {
    const targetId =
      root?.getAttribute('data-btus-anchor-id') ||
      'bt-urun-sinerji';

    const blocks = document.querySelectorAll('[data-btus-anchor-block]');

    blocks.forEach((block) => {
      const blockTarget = block.getAttribute('data-btus-anchor-target') || 'bt-urun-sinerji';
      block.hidden = blockTarget !== targetId || !isVisible;
    });
  };

  const bindAnchorBlocks = () => {
    if (document.documentElement.dataset.btusAnchorBound === 'true') {
      return;
    }

    document.documentElement.dataset.btusAnchorBound = 'true';

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-btus-anchor-block]');

      if (!trigger || trigger.hidden) return;

      const targetId = trigger.getAttribute('data-btus-anchor-target') || 'bt-urun-sinerji';
      const anchorTarget = document.getElementById(targetId);
      const synergySection = document.querySelector('[data-bt-urun-sinerji]:not([hidden])');
      const target = anchorTarget || synergySection;

      if (!target) return;

      event.preventDefault();

      const headerOffset = window.matchMedia('(max-width: 767px)').matches ? 78 : 96;
      const targetTop = target.getBoundingClientRect().top + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: Math.max(targetTop, 0),
        behavior: 'smooth'
      });
    });
  };

  const createWarningCard = (root, warning) => {
    const template = root.querySelector('[data-btus-warning-template]');
    if (!template) return null;

    const node = template.content.firstElementChild.cloneNode(true);

    const name = node.querySelector('[data-btus-warning-name]');
    const severity = node.querySelector('[data-btus-warning-severity]');
    const message = node.querySelector('[data-btus-warning-message]');

    if (name) {
      name.textContent = warning.name || 'Kullanım notu';
    }

    if (severity) {
      const label = severityLabel(warning.severity);

      severity.textContent = label;
      severity.hidden = !label;
    }

    if (message) {
      message.textContent = warning.message || '';
    }

    return node;
  };

  const createCompareElement = (root, productHandle, labelText) => {
    const template = root.querySelector('[data-btus-compare-template]');
    let wrapper;

    if (template) {
      wrapper = template.content.firstElementChild.cloneNode(true);
    } else {
      wrapper = document.createElement('sc-add-to-compare');

      const input = document.createElement('input');
      const label = document.createElement('label');

      input.type = 'checkbox';

      wrapper.append(input, label);
    }

    wrapper.setAttribute('product-handle', productHandle);

    const input = wrapper.querySelector('input[type="checkbox"]');
    const label = wrapper.querySelector('label');

    if (input) {
      const safeHandle = String(productHandle || '').replace(/[^a-zA-Z0-9_-]/g, '-');
      const uniqueId = `btus-compare-${safeHandle}-${Math.random().toString(36).slice(2, 8)}`;

      input.id = uniqueId;
      input.name = `compare-${safeHandle}`;
      input.setAttribute('aria-label', labelText || 'Karşılaştır');

      input.addEventListener('change', () => {
        const eventName = input.checked
          ? 'sc:dynamic-compare-add'
          : 'sc:dynamic-compare-remove';

        window.dispatchEvent(
          new CustomEvent(eventName, {
            detail: {
              handle: productHandle,
              productHandle
            }
          })
        );
      });
    }

    if (label) {
      label.textContent = labelText || 'Karşılaştır';

      if (input) {
        label.setAttribute('for', input.id);
      }
    }

    return wrapper;
  };

  const showToast = (message) => {
    const existing = document.querySelector('.bt-urun-sinerji__toast');

    if (existing) {
      existing.remove();
    }

    const toast = document.createElement('div');

    toast.className = 'bt-urun-sinerji__toast';
    toast.textContent = message;

    document.body.appendChild(toast);

    setTimeout(() => {
      toast.remove();
    }, 2600);
  };

  const createAddButton = (product, settings) => {
    const button = document.createElement('button');

    button.type = 'button';
    button.className = 'bt-urun-sinerji__button bt-urun-sinerji__button--primary';
    button.textContent = settings.addButtonText || 'Sepete Ekle';

    if (!product.available) {
      button.disabled = true;
      button.classList.add('is-disabled');
      button.textContent = 'Stokta Yok';

      return button;
    }

    button.addEventListener('click', async () => {
      button.disabled = true;

      const previousText = button.textContent;

      button.textContent = 'Ekleniyor...';

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            id: product.variantId,
            quantity: 1
          })
        });

        if (!response.ok) {
          throw new Error('Sepete eklenemedi');
        }

        document.dispatchEvent(
          new CustomEvent('btus:cart:add', {
            detail: {
              product
            }
          })
        );

        document.dispatchEvent(new CustomEvent('cart:refresh'));

        if (settings.afterAdd === 'cart_page') {
          window.location.href = '/cart';
          return;
        }

        if (settings.afterAdd === 'toast') {
          showToast(`${product.title} sepete eklendi.`);
        }

        button.textContent = 'Eklendi';

        setTimeout(() => {
          button.textContent = previousText;
          button.disabled = false;
        }, 1200);
      } catch (error) {
        button.textContent = 'Tekrar Dene';
        button.disabled = false;

        showToast('Ürün sepete eklenemedi. Lütfen tekrar deneyin.');
      }
    });

    return button;
  };

  const createSynergyCard = (root, product, settings) => {
    const template = root.querySelector('[data-btus-synergy-template]');

    if (!template) return null;

    const node = template.content.firstElementChild.cloneNode(true);

    const media = node.querySelector('[data-btus-product-url]');
    const image = node.querySelector('[data-btus-product-image]');
    const title = node.querySelector('[data-btus-product-title]');
    const price = node.querySelector('[data-btus-product-price]');
    const reason = node.querySelector('[data-btus-synergy-reason]');
    const actions = node.querySelector('[data-btus-actions]');

    if (media) {
      media.href = product.url || `/products/${product.handle}`;
      media.target = '_blank';
      media.rel = 'noopener noreferrer';
    }

    if (image) {
      if (product.image) {
        image.src = product.image;
        image.alt = product.title || '';
        image.loading = 'lazy';
        image.decoding = 'async';
        image.width = 800;
        image.height = 800;
        image.sizes = '(max-width: 767px) 76vw, (max-width: 989px) 42vw, (max-width: 1199px) 118px, 128px';
      } else {
        image.remove();
      }
    }

    if (title) {
      title.href = product.url || `/products/${product.handle}`;
      title.target = '_blank';
      title.rel = 'noopener noreferrer';
      title.textContent = product.title || '';
    }

    if (price) {
      const showPrice = settings.showPrice !== false && settings.showPrice !== 'false';

      if (showPrice) {
        price.textContent = product.price || '';
      } else {
        price.remove();
      }
    }

    if (reason) {
      reason.textContent = product.synergyRule?.name || 'Tamamlayıcı içerik';
    }

    if (actions) {
      const buttonType = settings.buttonType || 'ikisi_de';

      if (buttonType === 'gosterme') {
        actions.remove();
        return node;
      }

      const addButton = createAddButton(product, settings);

      const compareButton = createCompareElement(
        root,
        product.handle,
        settings.compareButtonText || 'Karşılaştır'
      );

      const order = settings.buttonOrder || 'sepet_once';

      if (buttonType === 'sepete_ekle') {
        actions.appendChild(addButton);
      } else if (buttonType === 'karsilastir') {
        actions.appendChild(compareButton);
      } else if (order === 'karsilastir_once') {
        actions.append(compareButton, addButton);
      } else {
        actions.append(addButton, compareButton);
      }
    }

    return node;
  };

  const renderSynergyProducts = (root, products, settings) => {
    const synergiesTarget = root.querySelector('[data-btus-synergies]');

    if (!synergiesTarget) {
      return false;
    }

    synergiesTarget.innerHTML = '';

    if (!products.length) {
      return false;
    }

    products.forEach((product) => {
      const card = createSynergyCard(root, product, settings);

      if (card) {
        synergiesTarget.appendChild(card);
      }
    });

    return true;
  };

  const render = (root, config) => {
    const currentVariantId = getCurrentVariantId(root, config.selectedVariantId);

    const activeKeys = unique(
      config.variantKeys?.[currentVariantId] ||
      config.variantKeys?.[String(currentVariantId)] ||
      []
    );

    const settings = config.settings || {};

    const content = root.querySelector('[data-btus-content]');
    const empty = root.querySelector('[data-btus-empty]');
    const intro = root.querySelector('[data-btus-intro]');
    const source = root.querySelector('[data-btus-source]');
    const noteCard = root.querySelector('[data-btus-note-card]');

    if (intro && !intro.dataset.btusDefaultIntro) {
      intro.dataset.btusDefaultIntro = intro.textContent.trim();
    }

    const warningBlock = root.querySelector('[data-btus-warning-block]');
    const warningsTarget = root.querySelector('[data-btus-warnings]');

    const synergyBlock = root.querySelector('[data-btus-synergy-block]');
    const synergiesTarget = root.querySelector('[data-btus-synergies]');

    if (warningsTarget) {
      warningsTarget.innerHTML = '';
    }

    if (synergiesTarget) {
      synergiesTarget.innerHTML = '';
    }

    const { warnings, synergies } = findMatchingRules(config.rules, activeKeys);

    const normalizedActiveKeys = unique(
      (activeKeys || []).map(normalizeKey)
    );

    const productPool = getCandidateProductPool(
      synergies,
      config.candidates,
      normalizedActiveKeys,
      settings
    );

    const pageIndex = Number(root.dataset.btusSuggestionPage || 0);
    const products = getProductsForPage(productPool, settings, pageIndex);

    const primarySynergyMessage =
      products.find((product) => product.synergyRule?.message)
        ?.synergyRule?.message || '';

    if (intro) {
      intro.textContent =
        primarySynergyMessage ||
        intro.dataset.btusDefaultIntro ||
        '';
    }

    if (source) {
      const primarySourceRule =
        products.find((product) => product.synergyRule?.source || product.synergyRule?.sourceName)
          ?.synergyRule || null;

      const primarySource =
        primarySourceRule?.sourceName ||
        primarySourceRule?.source ||
        '';

      const primarySourceUrl = primarySourceRule?.sourceUrl || '';

      source.textContent = '';

      if (primarySource) {
        const labelText = `Bilimsel Kaynak: ${primarySource}`;

        if (primarySourceUrl) {
          const link = document.createElement('a');
          link.className = 'bt-urun-sinerji__source-link';
          link.href = primarySourceUrl;
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          link.setAttribute('aria-label', `${labelText} - yeni sekmede açılır`);

          const text = document.createElement('span');
          text.textContent = labelText;

          const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
          icon.setAttribute('viewBox', '0 0 24 24');
          icon.setAttribute('focusable', 'false');
          icon.setAttribute('aria-hidden', 'true');
          icon.classList.add('bt-urun-sinerji__source-link-icon');

          const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
          path.setAttribute('d', 'M14 3h7v7h-2V6.41l-9.29 9.3-1.42-1.42 9.3-9.29H14V3ZM5 5h6v2H7v10h10v-4h2v6H5V5Z');

          icon.appendChild(path);
          link.append(text, icon);
          source.appendChild(link);
        } else {
          source.textContent = labelText;
        }
      }

      source.hidden = !primarySource;
    }

    if (noteCard) {
      const hasIntroText = Boolean(intro?.textContent?.trim());
      const hasSourceText = Boolean(source && !source.hidden);
      noteCard.hidden = !(hasIntroText || hasSourceText);
    }

    root.btusProductPool = productPool;

    let hasWarnings = false;
    let hasSynergies = false;

    if (toBool(settings.showWarnings) && warnings.length && warningsTarget) {
      warnings.forEach((warning) => {
        const card = createWarningCard(root, warning);

        if (card) {
          warningsTarget.appendChild(card);
        }
      });

      hasWarnings = true;
    }

    if (toBool(settings.showSynergy) && products.length && synergiesTarget) {
      hasSynergies = renderSynergyProducts(root, products, settings);
    }

    if (warningBlock) {
      warningBlock.hidden = !hasWarnings;
    }

    if (synergyBlock) {
      synergyBlock.hidden = !hasSynergies;
    }

    const refreshWrap = root.querySelector('[data-btus-refresh-wrap]');
    const refreshButton = root.querySelector('[data-btus-refresh]');
    const maxProducts = getResponsiveMaxProducts(settings);

    if (refreshWrap) {
      refreshWrap.hidden = !(hasSynergies && productPool.length > maxProducts);
    }

    if (refreshButton) {
      refreshButton.textContent =
        settings.refreshButtonText || refreshButton.textContent || 'Farklı öneriler göster';
    }

    const hasRenderableContent = hasWarnings || hasSynergies;

    root.hidden = !hasRenderableContent;
    updateAnchorBlocks(root, hasRenderableContent);

    if (content) {
      content.hidden = !hasRenderableContent;
    }

    if (empty) {
      const emptyEnabled = empty.dataset.emptyEnabled === 'true';

      empty.hidden = hasRenderableContent || !emptyEnabled;
    }

    root.dispatchEvent(
      new CustomEvent('btus:rendered', {
        bubbles: true,
        detail: {
          activeKeys,
          warnings,
          synergies,
          products
        }
      })
    );
  };

  const bindVariantChanges = (root, config) => {
    const productSection = root.closest('section');

    const form =
      productSection?.querySelector('form[action*="/cart/add"]') ||
      document.querySelector('form[action*="/cart/add"]');

    if (!form) return;

    let lastVariantId = getCurrentVariantId(root, config.selectedVariantId);

    const scheduleRender = () => {
      window.requestAnimationFrame(() => {
        const nextVariantId = getCurrentVariantId(root, config.selectedVariantId);

        if (nextVariantId !== lastVariantId) {
          lastVariantId = nextVariantId;
          root.dataset.btusSuggestionPage = '0';
          render(root, config);
        }
      });
    };

    form.addEventListener('change', scheduleRender, true);
    form.addEventListener('input', scheduleRender, true);

    document.addEventListener('variant:change', scheduleRender);
    document.addEventListener('product:variant-change', scheduleRender);
  };

  const resetMobileCarouselScroll = (root) => {
    if (
      !root ||
      typeof window === 'undefined' ||
      !window.matchMedia ||
      !window.matchMedia('(max-width: 767px)').matches
    ) {
      return;
    }

    const grid = root.querySelector('[data-btus-synergies]');

    if (!grid) return;

    const reset = (behavior = 'auto') => {
      if (typeof grid.scrollTo === 'function') {
        grid.scrollTo({
          left: 0,
          behavior
        });
      }

      grid.scrollLeft = 0;
    };

    requestAnimationFrame(() => {
      reset('smooth');

      requestAnimationFrame(() => {
        reset('auto');
      });
    });

    window.setTimeout(() => {
      reset('auto');
    }, 180);
  };

  const bindRefreshButton = (root, config) => {
    const button = root.querySelector('[data-btus-refresh]');

    if (!button || button.dataset.btusRefreshBound === 'true') return;

    button.dataset.btusRefreshBound = 'true';

    button.addEventListener('click', () => {
      const settings = config.settings || {};
      const pool = root.btusProductPool || [];
      const maxProducts = getResponsiveMaxProducts(settings);

      if (!pool.length || pool.length <= maxProducts) return;

      const currentPage = Number(root.dataset.btusSuggestionPage || 0);
      const nextPage = currentPage + 1;

      root.dataset.btusSuggestionPage = String(nextPage);

      const products = getProductsForPage(pool, settings, nextPage);

      renderSynergyProducts(root, products, settings);

      resetMobileCarouselScroll(root);

      const intro = root.querySelector('[data-btus-intro]');
      const primarySynergyMessage =
        products.find((product) => product.synergyRule?.message)
          ?.synergyRule?.message || '';

      if (intro) {
        intro.textContent =
          primarySynergyMessage ||
          intro.dataset.btusDefaultIntro ||
          '';
      }

      const source = root.querySelector('[data-btus-source]');
      const noteCard = root.querySelector('[data-btus-note-card]');

      if (source) {
        const primarySourceRule =
          products.find((product) => product.synergyRule?.source || product.synergyRule?.sourceName)
            ?.synergyRule || null;

        const primarySource =
          primarySourceRule?.sourceName ||
          primarySourceRule?.source ||
          '';

        const primarySourceUrl = primarySourceRule?.sourceUrl || '';

        source.textContent = '';

        if (primarySource) {
          const labelText = `Bilimsel Kaynak: ${primarySource}`;

          if (primarySourceUrl) {
            const link = document.createElement('a');
            link.className = 'bt-urun-sinerji__source-link';
            link.href = primarySourceUrl;
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
            link.setAttribute('aria-label', `${labelText} - yeni sekmede açılır`);

            const text = document.createElement('span');
            text.textContent = labelText;

            const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
            icon.setAttribute('viewBox', '0 0 24 24');
            icon.setAttribute('focusable', 'false');
            icon.setAttribute('aria-hidden', 'true');
            icon.classList.add('bt-urun-sinerji__source-link-icon');

            const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
            path.setAttribute('d', 'M14 3h7v7h-2V6.41l-9.29 9.3-1.42-1.42 9.3-9.29H14V3ZM5 5h6v2H7v10h10v-4h2v6H5V5Z');

            icon.appendChild(path);
            link.append(text, icon);
            source.appendChild(link);
          } else {
            source.textContent = labelText;
          }
        }

        source.hidden = !primarySource;
      }

      if (noteCard) {
        const hasIntroText = Boolean(intro?.textContent?.trim());
        const hasSourceText = Boolean(source && !source.hidden);
        noteCard.hidden = !(hasIntroText || hasSourceText);
      }
    });
  };

  const bindResponsiveProductCount = (root, config) => {
    if (root.dataset.btusResponsiveBound === 'true') return;

    root.dataset.btusResponsiveBound = 'true';

    let lastMode = window.matchMedia('(max-width: 767px)').matches
      ? 'mobile'
      : window.matchMedia('(max-width: 989px)').matches
        ? 'tablet'
        : 'desktop';

    window.addEventListener('resize', () => {
      const nextMode = window.matchMedia('(max-width: 767px)').matches
        ? 'mobile'
        : window.matchMedia('(max-width: 989px)').matches
          ? 'tablet'
          : 'desktop';

      if (nextMode === lastMode) return;

      lastMode = nextMode;
      root.dataset.btusSuggestionPage = '0';
      render(root, config);
    }, { passive: true });
  };

  const initRoot = (root) => {
    bindAnchorBlocks();

    if (!root || root.dataset.btusInitialized === 'true') return;

    const script = root.querySelector(CONFIG_SELECTOR);

    if (!script) return;

    let config;

    try {
      config = JSON.parse(script.textContent || '{}');
    } catch (error) {
      console.warn('[BT Ürün Sinerji] JSON config okunamadı', error);
      return;
    }

    root.dataset.btusInitialized = 'true';

    const empty = root.querySelector('[data-btus-empty]');

    if (empty && !empty.hasAttribute('hidden')) {
      empty.dataset.emptyEnabled = 'true';
    }

    render(root, config);
    bindRefreshButton(root, config);
    bindResponsiveProductCount(root, config);
    bindVariantChanges(root, config);
  };

  const initAll = () => {
    document.querySelectorAll(ROOT_SELECTOR).forEach(initRoot);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target?.querySelector?.(ROOT_SELECTOR);

    if (root) {
      root.dataset.btusInitialized = 'false';
      initRoot(root);
    }
  });
})();
